<template>
    <div class="model-portfolio-details">
        <div class="row">
            <div class="col">
                <template v-if="isModelPortfolioAvailable">
                    <b-tabs
                        fill
                        nav-class="model-portfolio-details__tabs"
                        content-class="model-portfolio-details__content">
                        <template v-for="filter in [DetailsFilterType.ASSET_CLASS, DetailsFilterType.CURRENCY, DetailsFilterType.REGION]">
                            <b-tab
                                title-link-class="model-portfolio-details__tab"
                                :title="$t(`modelPortfolioDetails.type.${filter}`)"
                                :key="filter"
                                @click="onSetSelectedDetailsFilterType(filter)" />
                        </template>
                        <div class="row model-portfolio__asset-allocation">
                            <model-portfolio-asset-allocation-chart class="col-lg-4" />
                            <model-portfolio-asset-allocation-table
                                v-if="selectedDetailsSubFilterType && modelPortfolioDetailsWithColorsAndIcons"
                                class="col-lg-8"
                                :selected-sub-filter-type="selectedDetailsSubFilterType"
                                :rows="modelPortfolioDetailsWithColorsAndIcons"
                                @onChangeSubFilter="handleSubFilterChange" />
                        </div>
                        <model-portfolio-positions
                            v-if="modelPortfolioPositions"
                            :rows="sortedAndFilteredModelPortfolioPositions"
                            :filter-type="modelPortfolioPositionDtoFilterType" />
                    </b-tabs>
                </template>
                <template v-else>
                    <p>
                        {{ $t("modelPortfolioDetails.noPortfolioAvailableMessage") }}
                    </p>
                </template>
            </div>
        </div>
    </div>
</template>

<script>
import { DetailsFilterType, DetailsSubFilterType } from '@/api/enums';
import { mapState, mapActions, mapGetters } from 'vuex';
import ModelPortfolioPositions from './ModelPortfolioPositions';
import ModelPortfolioAssetAllocationTable from '@/components/ModelPortfolioAssetAllocationTable';
import ModelPortfolioAssetAllocationChart from '@/components/ModelPortfolioAssetAllocationChart';

export default {
    name: 'ModelPortfolioDetails',
    components: {
        ModelPortfolioAssetAllocationTable,
        ModelPortfolioAssetAllocationChart,
        ModelPortfolioPositions,
    },
    data () {
        return {
            DetailsFilterType,
            DetailsSubFilterType,
        };
    },
    computed: {
        ...mapState(['selectedDetailsFilterType', 'selectedDetailsSubFilterType', 'modelPortfolioDetails', 'modelPortfolioPositions']),
        ...mapGetters(['isModelPortfolioAvailable']),
        modelPortfolioDetailsWithColorsAndIcons () {
            return this.modelPortfolioDetails
                ? this.modelPortfolioDetails.map((item, index) => {
                    return {
                        name: item.name,
                        percentage: item.percentage,
                        icon: this.getIcon(item.detailsSubFilterType),
                        colorClass: this.getColorClass(index),
                        detailsSubFilterType: item.detailsSubFilterType,
                    };
                })
                    .sort((pos1, pos2) => DetailsSubFilterType[pos1.detailsSubFilterType].order < DetailsSubFilterType[pos2.detailsSubFilterType].order ? -1 : 1)
                : null;
        },
        modelPortfolioPositionDtoFilterType () {
            switch (this.selectedDetailsFilterType) {
                case DetailsFilterType.ASSET_CLASS:
                    return 'assetGroupType';
                case DetailsFilterType.CURRENCY:
                    return 'currencyType';
                case DetailsFilterType.REGION:
                    return 'regionType';
                default:
                    // TODO fix this
                    return undefined;
            }
        },
        sortedAndFilteredModelPortfolioPositions () {
            return this.modelPortfolioPositions
                .filter(position => position[this.modelPortfolioPositionDtoFilterType] === this.selectedDetailsSubFilterType);
        },
    },
    methods: {
        ...mapActions(['setSelectedDetailsFilterType', 'setSelectedDetailsSubFilterType', 'fetchModelPortfolioDetails', 'fetchModelPortfolioPositions']),
        onSetSelectedDetailsFilterType (type) {
            this.setSelectedDetailsFilterType(type);
            this.setSelectedDetailsSubFilterType(this.getDefaultSelectedSubFilter(type));
            this.fetchModelPortfolioDetails();
        },
        getColorClass (index) {
            return `details-sub-filter-color--${index}`;
        },
        getIcon (detailsSubFilterType) {
            return `details-${detailsSubFilterType.toLowerCase().replace('_', '-')}`;
        },
        handleSubFilterChange (newSubFilter) {
            this.setSelectedDetailsSubFilterType(newSubFilter.detailsSubFilterType);
        },
        getDefaultSelectedSubFilter (newFilter) {
            switch (newFilter) {
                case DetailsFilterType.ASSET_CLASS:
                    return DetailsSubFilterType.SHARES.name;
                case DetailsFilterType.CURRENCY:
                    return DetailsSubFilterType.CHF.name;
                case DetailsFilterType.REGION:
                    return DetailsSubFilterType.SWITZERLAND.name;
                default:
                    this.selectedDetailsSubFilterType = this.modelPortfolioDetails[0].detailsSubFilterType;
            }
        },
    },
    created () {
        this.onSetSelectedDetailsFilterType(DetailsFilterType.ASSET_CLASS);
        this.fetchModelPortfolioPositions();
    },
};
</script>

<style scoped lang="scss">
.model-portfolio-details {
  @include font-migrosbank();
  margin-top: 20px;

  .model-portfolio__asset-allocation {
    margin-bottom: 25px;
  }

  ::v-deep .model-portfolio-details__tab {
    @include header-3();
    line-height: 1.3;
    color: $mb-grey-13;
    text-align: left;
    border-color: transparent;
    border-bottom: 1px solid $mb-grey-13;
    margin-right: 15px;
    padding: 0;

    @include media-breakpoint-down(xs) {
      line-height: 1.3;
      font-size: 16px;
    }
  }

  ::v-deep .model-portfolio-details__tab.active,
  ::v-deep .model-portfolio-details__tab:hover {
    color: $mb-grey5;
    border-color: transparent;
    border-bottom: 1px solid $mb-grey5;
  }

  ::v-deep .model-portfolio-details__tabs {
    border: 0;
    width: 600px;
    margin-bottom: 20px;
    flex-wrap: nowrap;

    @include media-breakpoint-down(xs) {
      width: 100%;
      margin-bottom: 25px;
    }
  }
}
</style>
